import { useCallback, useContext } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import ModalContext from 'contexts/ModalContext'
import { selectLoggedIn } from 'selectors/accountSelectors'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import type { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal.types'
import LoyaltyJoinModal from 'loyalty/components/LoyaltyJoinModal'
import enrolLoyaltyAccount from 'actions/loyalty/enrolLoyaltyAccount'
import getCanJoinLoyalty from 'loyalty/selectors/getCanJoinLoyalty'

/**
 * Shared "Join Société for free" button copy. Used by the join-Société flow itself and reused by the
 * surfaces that offer it (renewal bonus modal, reactivation success screen) so the CTA never drifts.
 */
export const joinSocieteMessages = defineMessages({
  cta: {
    id: 'qSHLLz7Fi2nt6ie1HCWT',
    defaultMessage: 'Join Société for free',
    description: 'Shared button/CTA label for joining the free Société loyalty program, reused across LuxPlus+ renewal surfaces',
  },
})

/**
 * Returns a callback that starts the existing join-Société flow: logged-out users sign in and are
 * then enrolled, logged-in non-members open the loyalty join modal and, on confirmation, enrol.
 * Mirrors the other "Join Société" CTAs (e.g. LoyaltyPurchasePointsNeedsMembershipCta). Does nothing for a logged-in customer
 * who can't join (e.g. a closed account); surfaces should hide their join CTA for them anyway.
 */
export default function useLoyaltyEnrolExistingUser() {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const showModal = useContext(ModalContext)
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const canJoinLoyalty = useAppSelector(getCanJoinLoyalty)

  return useCallback(async() => {
    if (!isLoggedIn) {
      // Keeps sign-in auto-enrolment out of the way, so they're enrolled here as CUSTOMER
      const loginResult = await showModal<AccountAccessModalResult>(<AccountAccessModal skipLoyaltyAutoEnrolment />)
      if (loginResult?.loggedIn) {
        dispatch(enrolLoyaltyAccount({ enrolment: 'existingUserRegistration' }))
      }
      return
    }
    if (!canJoinLoyalty) return
    const joinConfirmed = await showModal<boolean>(
      <LoyaltyJoinModal onAfterClose={() => {}} primaryActionCta={intl.formatMessage(joinSocieteMessages.cta)} />,
    )
    if (joinConfirmed) {
      dispatch(enrolLoyaltyAccount({ enrolment: 'existingUserRegistration' }))
    }
  }, [isLoggedIn, canJoinLoyalty, showModal, dispatch, intl])
}
